import { useState } from "react";
import type { ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { MenuItem } from "@/components/design/MenuItem";
import { MultiSelect } from "@/components/design/MultiSelect";
import {
  isFieldActive,
  clearField,
  fieldSummary,
  type FilterField,
  type OptionsByKey,
} from "@/lib/filter-fields";

// The extended search's filter-pill UI, shared by every search category that has facets.
//
// It was the Teamplayer builder first and is deliberately NOT copied per category: the repo's rule
// is to deepen an existing primitive rather than grow a second one (CLAUDE.md, "Reuse over
// proliferation"). Nothing in here ever knew what a person was — every read already went through a
// `Record<string, …>` cast on the field descriptor — so making it generic removed casts rather than
// adding them. Each category supplies its own field list, its own heading and its own option lists;
// PersonFilterBuilder and CompanyFilterBuilder are the two thin wrappers that do so.

export function FilterBuilder<T extends object>({
  fields,
  frequentKeys,
  heading,
  value: person,
  setValue: setPerson,
  optionsByKey,
  onEnter,
  compact = false,
}: {
  fields: readonly FilterField[];
  /** Field keys to lift above the divider in the "+ Add filter" menu. */
  frequentKeys: readonly string[];
  /** The panel's own title, already localized and already carrying its active count. */
  heading: ReactNode;
  value: T;
  setValue: React.Dispatch<React.SetStateAction<T>>;
  optionsByKey: OptionsByKey;
  onEnter?: () => void;
  compact?: boolean;
}) {
  const intl = useIntl();
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>(() =>
    fields.filter((f) => isFieldActive(f, person)).map((f) => f.key),
  );

  const setP = (key: string) => (value: unknown) =>
    setPerson((f) => ({ ...f, [key]: value } as T));

  const displayedFields = selected
    .map((key) => fields.find((f) => f.key === key))
    .filter((f): f is FilterField => Boolean(f));
  const availableFields = fields.filter((f) => !selected.includes(f.key));
  const frequentAvailable = availableFields.filter((f) => frequentKeys.includes(f.key));
  const restAvailable = availableFields.filter((f) => !frequentKeys.includes(f.key));
  const editingField = fields.find((f) => f.key === editingKey) ?? null;

  const openEditor = (key: string) => {
    setEditingKey(key);
    setAddOpen(false);
  };

  const addField = (key: string) => {
    setSelected((s) => (s.includes(key) ? s : [...s, key]));
    setAddOpen(false);
    setEditingKey(key);
  };

  const removeField = (field: FilterField) => {
    setSelected((s) => s.filter((k) => k !== field.key));
    setPerson((f) => clearField(field, f));
    setEditingKey((k) => (k === field.key ? null : k));
  };

  return (
    <div className={compact ? "" : "px-5 py-4"}>
      <div className="mb-3 text-[10px] tracking-[0.25em] text-ink/45">{heading}</div>

      <div className="flex flex-wrap items-center gap-1.5">
        {displayedFields.map((f) => {
          const value = isFieldActive(f, person) ? fieldSummary(f, person, optionsByKey) : null;
          const editing = editingKey === f.key;
          const fieldLabel = intl.formatMessage({ id: f.labelId, defaultMessage: f.key });
          return (
            <span
              key={f.key}
              className={`inline-flex items-center rounded-full py-1 pl-2.5 pr-1 text-[11px] transition ${
                editing ? "ring-1 ring-accent/50 " : ""
              }${value ? "bg-ink/[0.06] text-ink/70" : "border border-dashed border-ink/30 text-ink/55"}`}
            >
              <button
                type="button"
                onClick={() => openEditor(f.key)}
                className="inline-flex min-w-0 items-center gap-1"
              >
                <span className="text-ink/45">
                  {fieldLabel}
                  {value ? ":" : ""}
                </span>
                {value ? <span className="max-w-[220px] truncate text-ink/70">{value}</span> : null}
              </button>
              <button
                type="button"
                aria-label={intl.formatMessage(
                  { id: "search.removeFieldLabel", defaultMessage: "Remove {field}" },
                  { field: fieldLabel },
                )}
                onClick={() => removeField(f)}
                className="ml-1 grid h-4 w-4 shrink-0 place-items-center rounded-full text-ink/40 transition hover:bg-ink/10 hover:text-ink"
              >
                <span className="block text-[13px] leading-none">×</span> {/* i18n-allow */}
              </button>
            </span>
          );
        })}

        <div className="relative">
          <Button
            variant="chip"
            size="sm"
            disabled={availableFields.length === 0}
            onClick={() => {
              setAddOpen((o) => !o);
              setEditingKey(null);
            }}
          >
            <FormattedMessage id="personFilter.addFilter" defaultMessage="+ Add filter" />
          </Button>
          {addOpen ? (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setAddOpen(false)} />
              <div className="absolute left-0 top-full z-30 mt-1 max-h-80 w-56 overflow-auto rounded-md bg-page py-1 shadow-lg ring-1 ring-ink/10">
                {availableFields.length === 0 ? (
                  <div className="px-3 py-1.5 text-[12px] text-ink/40">
                    <FormattedMessage id="search.allFiltersAdded" defaultMessage="All filters added" />
                  </div>
                ) : (
                  <>
                    {frequentAvailable.length > 0 ? (
                      <>
                        <div className="px-3 pb-1 pt-1.5 text-[9px] uppercase tracking-[0.2em] text-ink/40">
                          <FormattedMessage id="personFilter.frequent" defaultMessage="Frequent" />
                        </div>
                        {frequentAvailable.map((f) => (
                          <MenuItem
                            key={f.key}
                            onClick={() => addField(f.key)}
                          >
                            {intl.formatMessage({ id: f.labelId, defaultMessage: f.key })}
                          </MenuItem>
                        ))}
                      </>
                    ) : null}
                    {frequentAvailable.length > 0 && restAvailable.length > 0 ? (
                      <div className="my-1 border-t border-ink/5" />
                    ) : null}
                    {restAvailable.length > 0 ? (
                      <>
                        <div className="px-3 pb-1 pt-1.5 text-[9px] uppercase tracking-[0.2em] text-ink/40">
                          <FormattedMessage id="personFilter.allFilters" defaultMessage="All filters" />
                        </div>
                        {restAvailable.map((f) => (
                          <MenuItem
                            key={f.key}
                            onClick={() => addField(f.key)}
                          >
                            {intl.formatMessage({ id: f.labelId, defaultMessage: f.key })}
                          </MenuItem>
                        ))}
                      </>
                    ) : null}
                  </>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>

      {editingField ? (
        <FilterEditor
          field={editingField}
          person={person}
          setP={setP}
          optionsByKey={optionsByKey}
          onClear={() => removeField(editingField)}
          onDone={() => setEditingKey(null)}
          onEnter={() => {
            setEditingKey(null);
            onEnter?.();
          }}
        />
      ) : null}
    </div>
  );
}

function FilterEditor({
  field,
  person,
  setP,
  optionsByKey,
  onClear,
  onDone,
  onEnter,
}: {
  field: FilterField;
  person: Record<string, unknown> | object;
  setP: (key: string) => (value: unknown) => void;
  optionsByKey: OptionsByKey;
  onClear: () => void;
  onDone: () => void;
  onEnter: () => void;
}) {
  const intl = useIntl();
  return (
    <div className="mt-3 w-full max-w-sm rounded-md bg-ink/[0.06] p-3 ring-1 ring-ink/5">
      <div className="mb-2 flex items-center justify-between gap-4">
        <span className="text-[10px] tracking-[0.15em] text-ink/45">
          <FormattedMessage id={field.labelId} defaultMessage={field.key} />
        </span>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onClear}
            className="text-[10px] tracking-[0.15em] text-ink/40 hover:text-accent"
          >
            <FormattedMessage id="common.remove" defaultMessage="Remove" />
          </button>
          <button
            type="button"
            onClick={onDone}
            className="text-[10px] tracking-[0.15em] text-ink/55 hover:text-ink"
          >
            <FormattedMessage id="search.done" defaultMessage="Done" />
          </button>
        </div>
      </div>

      {field.kind === "text" ? (
        <FilterTextInput
          value={(person as unknown as Record<string, string>)[field.key] ?? ""}
          onChange={(v) => setP(field.key)(v)}
          onEnter={onEnter}
          autoFocus
        />
      ) : null}
      {field.kind === "textList" ? (
        <TextListInput
          values={(person as unknown as Record<string, readonly string[]>)[field.field!] ?? []}
          onChange={(next) => setP(field.field!)(next)}
          onEnterEmpty={onEnter}
        />
      ) : null}
      {field.kind === "range" ? (
        <RangeRow
          min={(person as unknown as Record<string, number>)[field.min!] ?? field.lo!}
          max={(person as unknown as Record<string, number>)[field.max!] ?? field.hi!}
          lo={field.lo!}
          hi={field.hi!}
          onMin={(v) => setP(field.min!)(v)}
          onMax={(v) => setP(field.max!)(v)}
        />
      ) : null}
      {field.kind === "multi" ? (
        <MultiSelect
          options={optionsByKey[field.optionsKey!] ?? []}
          selected={(person as unknown as Record<string, readonly string[]>)[field.field!] ?? []}
          onChange={(arr) => setP(field.field!)(arr)}
          placeholder={intl.formatMessage({ id: "search.any", defaultMessage: "Any" })}
          searchable={field.searchable}
        />
      ) : null}
    </div>
  );
}

function FilterTextInput({
  value,
  onChange,
  onEnter,
  onBlur,
  placeholder,
  autoFocus = false,
}: {
  value: string;
  onChange: (next: string) => void;
  onEnter?: () => void;
  /** Called when focus leaves, so a typed-but-uncommitted term is not silently lost. */
  onBlur?: () => void;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  return (
    <input
      type="text"
      autoFocus={autoFocus}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      onKeyDown={(e) => {
        if (e.key === "Enter" && onEnter) onEnter();
      }}
      className="w-full rounded bg-page px-2 py-1.5 text-[13px] text-ink ring-1 ring-ink/10 placeholder:text-ink/40 focus:outline-none focus:ring-1 focus:ring-accent"
    />
  );
}

/**
 * Several free-text terms, entered one at a time.
 *
 * A text box that commits on Enter rather than a picker, because the values are open-ended — there
 * is no catalogue to offer, and a searchable picker was the version that confused: typing into one
 * only filters its list, so nothing was ever set. Committed terms render as removable chips, using
 * the same chip shape MultiSelect already uses for its selection, so the two controls read alike.
 *
 * Enter on an EMPTY box runs the panel's own submit, so the key does the expected thing whether the
 * user is still adding terms or done adding them. LOSING FOCUS DOES NOT — see `keepDraft` below for
 * why the two gestures cannot share a handler.
 */
function TextListInput({
  values,
  onChange,
  onEnterEmpty,
}: {
  values: readonly string[];
  onChange: (next: string[]) => void;
  onEnterEmpty?: () => void;
}) {
  const intl = useIntl();
  const [draft, setDraft] = useState("");

  // Saves whatever is typed, and does NOTHING when the box is empty. This is what BLUR gets.
  const keepDraft = () => {
    const term = draft.trim();
    if (!term) return;
    // Case-insensitive de-duplication: the terms are matched case-insensitively downstream, so two
    // spellings of one word would be two chips that filter identically.
    if (!values.some((v) => v.toLowerCase() === term.toLowerCase())) {
      onChange([...values, term]);
    }
    setDraft("");
  };

  // What ENTER gets: the same save, plus "empty box means I am done" — which closes the editor and
  // runs the search.
  //
  // THE TWO ARE SEPARATE ON PURPOSE, and collapsing them back into one function reintroduces a real
  // bug. Enter is deliberate, so letting it mean "done" is fair. Blur is not: it fires whenever
  // focus leaves for any reason. While both shared one handler, clicking the x on a term chip took
  // focus off an empty box, which submitted and unmounted the editor BEFORE the click could land —
  // so the term was never removed and the panel just shut. That left no working way to drop one
  // term while keeping the others; the only x that did anything was the pill's, which clears them
  // all.
  const commitOrSubmit = () => {
    if (!draft.trim()) {
      onEnterEmpty?.();
      return;
    }
    keepDraft();
  };

  return (
    <div>
      {values.length > 0 ? (
        <div className="mb-2 flex flex-wrap gap-1">
          {values.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 rounded-full bg-ink/[0.06] px-2 py-0.5 text-[11px] text-ink/80"
            >
              {v}
              <button
                type="button"
                aria-label={intl.formatMessage(
                  { id: "search.removeTermLabel", defaultMessage: "Remove {term}" },
                  { term: v },
                )}
                onClick={() => onChange(values.filter((each) => each !== v))}
                className="cursor-pointer text-ink/40 hover:text-ink"
              >
                <span className="block text-[13px] leading-none">×</span> {/* i18n-allow */}
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <FilterTextInput
        value={draft}
        onChange={setDraft}
        onEnter={commitOrSubmit}
        onBlur={keepDraft}
        placeholder={intl.formatMessage({
          id: "search.addTermPlaceholder",
          defaultMessage: "Type a term, then press Enter",
        })}
        autoFocus
      />
    </div>
  );
}

function RangeRow({
  min,
  max,
  lo,
  hi,
  onMin,
  onMax,
}: {
  min: number;
  max: number;
  lo: number;
  hi: number;
  onMin: (next: number) => void;
  onMax: (next: number) => void;
}) {
  const clamp = (v: string, fallback: number): number => {
    const n = Number.parseInt(v, 10);
    if (Number.isNaN(n)) return fallback;
    return Math.min(hi, Math.max(lo, n));
  };
  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        min={lo}
        max={hi}
        value={min}
        onChange={(e) => onMin(clamp(e.target.value, lo))}
        className="w-full rounded bg-page px-2 py-1.5 text-[13px] text-ink ring-1 ring-ink/10 focus:outline-none focus:ring-1 focus:ring-accent"
      />
      <span className="text-[12px] text-ink/45">
        <FormattedMessage id="search.rangeTo" defaultMessage="to" />
      </span>
      <input
        type="number"
        min={lo}
        max={hi}
        value={max}
        onChange={(e) => onMax(clamp(e.target.value, hi))}
        className="w-full rounded bg-page px-2 py-1.5 text-[13px] text-ink ring-1 ring-ink/10 focus:outline-none focus:ring-1 focus:ring-accent"
      />
    </div>
  );
}
